import { useSyncExternalStore } from 'react';

import type { CalendarEventDto } from '@chat-emp/shared';

import { listEvents } from '../lib/facade.js';

/**
 * The employer's diary, fetched once and shared.
 *
 * On the module rather than in a provider because both halves of the Event
 * section show the same events — the list column and the grid beside it are two
 * views of one set, and two mounts must not be two fetches or two answers.
 *
 * Read-only, so none of `useLists`' optimistic-write machinery is here. The one
 * thing it does carry over is the loading flag: a not-yet-fetched store read as
 * an empty one would show "no events" to somebody who has ten.
 */

let snapshot: CalendarEventDto[] = [];
let loading = true;
let failed = false;
let hydrated = false;
const listeners = new Set<() => void>();

// One object per state change, not per read: useSyncExternalStore compares
// snapshots by identity, and a fresh literal on every getSnapshot is an infinite
// re-render.
let view = { events: snapshot, loading, failed };

function publish(): void {
  view = { events: snapshot, loading, failed };
  for (const listener of listeners) listener();
}

async function hydrate(): Promise<void> {
  try {
    snapshot = await listEvents();
    failed = false;
  } catch {
    // A later mount retries too, not only Try again. Never an empty diary: that would be a lie.
    snapshot = [];
    failed = true;
    hydrated = false;
  } finally {
    loading = false;
    publish();
  }
}

export function reloadEvents(): void {
  hydrated = true;
  loading = true;
  failed = false;
  publish();
  void hydrate();
}

/** Long enough to stay cheap, short enough that a jobseeker who joins shows up while the employer looks. */
export const EVENTS_POLL_MS = 30_000;
let poll: ReturnType<typeof setInterval> | undefined;

/** Hidden tabs skip the read; a tab that surfaces catches up at once. */
function tick(): void {
  if (!document.hidden) void refreshEvents();
}

function stopPolling(): void {
  clearInterval(poll);
  poll = undefined;
  document.removeEventListener('visibilitychange', tick);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!hydrated) reloadEvents();
  if (!poll) {
    poll = setInterval(tick, EVENTS_POLL_MS);
    document.addEventListener('visibilitychange', tick);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stopPolling();
  };
}

/**
 * Re-read the diary, keeping what is on screen if the re-read fails.
 *
 * Called after a booking lands, so the calendar shows what the server actually
 * stored rather than what the client hoped it sent. Deliberately not `hydrate`:
 * that one blanks the store and arms a retry on failure, which is right for a
 * first load and wrong here — the Chat Day was booked, and wiping a diary the
 * employer can see would report a success as a disaster.
 */
export async function refreshEvents(): Promise<void> {
  try {
    snapshot = await listEvents();
    failed = false;
    publish();
  } catch {
    // Leave the last good answer up. The next mount re-reads anyway.
  }
}

/**
 * What a gated read answers: an empty diary, settled, unfailed. One frozen
 * object for the same reason `view` is one — the snapshot is compared by
 * identity.
 */
const OFF = { events: [] as CalendarEventDto[], loading: false, failed: false };

/** Subscribing to nothing: an account without the feature has no diary to hear about. */
const ignore = (): (() => void) => () => {};

/**
 * @param enabled whether this account has the Event feature at all. Required,
 * not defaulted: an ungated call would fetch, the BFF would answer 403 (it gates
 * the same list server-side), and every boot would carry a guaranteed failed
 * request. There is no reading to be had, so there is no request to make.
 */
export function useEvents(enabled: boolean): {
  events: CalendarEventDto[];
  loading: boolean;
  failed: boolean;
} {
  return useSyncExternalStore(
    enabled ? subscribe : ignore,
    () => (enabled ? view : OFF),
    () => (enabled ? view : OFF),
  );
}

/** Test seam: drop the store so each case starts from nothing. */
export function resetEventsForTest(): void {
  snapshot = [];
  loading = true;
  failed = false;
  hydrated = false;
  listeners.clear();
  stopPolling();
  view = { events: snapshot, loading, failed };
}
